import { IconButton } from '@material-ui/core';
import { FC, useMemo } from 'react';

import { useOptions } from '@agency/contexts/OptionsContext';
import { LettingTransactionLeaseBreakStateItem } from '@agency/modules/leases/pages/letting-manage/pages/letting-lease-events/LettingLeaseEvents.types';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { DatePicker } from '@shared/components/date-picker';
import { NumberField } from '@shared/components/number-field';
import { SingleSelect } from '@shared/components/select-new/single-select';
import { TextField } from '@shared/components/text-field';
import { useAgencyConstants } from '@shared/contexts/ConfigContext';
import { t } from '@shared/i18n';
import { CloseIcon } from '@shared/icons/close';
import { ButtonSize } from '@shared/types/common/button';
import { InputSize } from '@shared/types/common/input';
import { getCurrencySymbol } from '@shared/utils/number';

import { styles } from './DisposalLeaseBreakCard.styles';

export interface DisposalLeaseBreakCardProps extends WithStyles<typeof styles> {
  currency: string;
  error: boolean;
  item: LettingTransactionLeaseBreakStateItem;
  onDelete: () => void;
  onUpdate: (item: Partial<LettingTransactionLeaseBreakStateItem>) => void;
}

const DisposalLeaseBreakCardComponent: FC<DisposalLeaseBreakCardProps> = ({
  classes,
  currency,
  error,
  item,
  onDelete,
  onUpdate,
}) => {
  const { constants } = useAgencyConstants();
  const { reminderDaysOptions, leaseBreakTypeOptions } = useOptions();

  const currencyAdornment = useMemo(
    () => <span className={classes.currencyAdornment}>{getCurrencySymbol(constants, currency)}</span>,
    [classes.currencyAdornment, constants, currency]
  );

  return (
    <div className={classes.root}>
      <div className={classes.cardHeader}>
        <IconButton onClick={onDelete} className={classes.deleteButton}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.grid}>
        <div className={classes.field}>
          <span className={classes.label}>{t('break_date')}</span>
          <DatePicker
            autoWidth={false}
            fullWidth
            placeholder={t('dd_or_mm_or_yyyy')}
            size={InputSize.medium}
            value={item.from}
            onChange={(value) => onUpdate({ from: value })}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{t('notice_period')}</span>
          <NumberField
            fullWidth
            placeholder={t('enter_notice')}
            InputProps={{ endAdornment: t('months') }}
            decimalPlaces={0}
            error={error}
            min={0}
            size={InputSize.medium}
            value={item.notice_months ?? ''}
            onChange={(value: string) => onUpdate({ notice_months: value })}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{t('type')}</span>
          <SingleSelect
            classes={{ root: classes.select }}
            placeholder={t('select_ellipsis')}
            size={ButtonSize.medium}
            error={error}
            value={item.break_option}
            options={leaseBreakTypeOptions}
            onChange={(value) => onUpdate({ break_option: value.toString() })}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{t('reminder')}</span>
          <SingleSelect
            classes={{ root: classes.select }}
            placeholder={t('select_ellipsis')}
            size={ButtonSize.medium}
            value={item.reminder_days}
            options={reminderDaysOptions}
            onChange={(value) => onUpdate({ reminder_days: Number(value) })}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{t('break_penalty')}</span>
          <NumberField
            fullWidth
            placeholder={t('enter_break_penalty')}
            InputProps={{ startAdornment: currencyAdornment }}
            min={0}
            size={InputSize.medium}
            value={item.break_penalty}
            onChange={(value: string) => onUpdate({ break_penalty: value })}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{t('break_penalty_comments')}</span>
          <TextField
            classes={{ root: classes.eventTextField }}
            multiline
            fullWidth
            minRows={1}
            placeholder={t('add_a_break_penalty_comment')}
            value={item.break_penalty_comments}
            onChange={(e) => onUpdate({ break_penalty_comments: e.target.value })}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{t('comments')}</span>
          <TextField
            classes={{ root: classes.eventTextField }}
            multiline
            fullWidth
            minRows={1}
            placeholder={t('add_a_comment')}
            value={item.comments}
            onChange={(e) => onUpdate({ comments: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};

export const DisposalLeaseBreakCard = withStyles(styles)(DisposalLeaseBreakCardComponent);
